/**
 * Pure quick-start-guide logic — Jason's direct spec tonight (LANE U1, piece
 * (b)): "a welcome to Mira... a quick start guide that CAN BE DISMISSED."
 * Kept framework-free (no React, no fetch) so the dismiss/re-enable decision
 * and every step's done-state derivation are directly unit-testable, same
 * house idiom onboardingStep.ts/homeRouteGate.ts/jobBuckets.ts already
 * establish for this app's pure modules (this repo has no jsdom/React-
 * Testing-Library setup).
 *
 * THE LAW THIS MODULE ENFORCES (NO INVENTION): every step's `done` flag is a
 * pure function of REAL served state the caller already has in hand
 * (roleCount from GET /corpus/roles, targetRoleCount from GET
 * /presence/target-roles, applicationCount from GET /applications) — never a
 * fabricated true. Absent/unknown state (null, per this app's "unknown, not
 * empty" discipline — see useHomeData.ts's own doc comment) reads as
 * not-done, the same as a genuine zero; this module never guesses.
 *
 * Six steps, mapping 1:1 onto Jason's own walkthrough words:
 *   1. upload your resume or paste your profile -> Refine
 *   2. talk it through / refine your story        -> Refine
 *   3. declare what you want to be found for       -> Get Found (Profiles)
 *   4. she watches the boards and brings you fits  -> Jobs (Discover)
 *   5. tailor + apply, always your call             -> Jobs (Discover)
 *   6. track every application                      -> Track (Tracker)
 */

export type QuickStartStepId =
  | 'upload-or-paste'
  | 'talk-it-through'
  | 'declare-target'
  | 'she-watches'
  | 'tailor-and-apply'
  | 'track-progress';

export const QUICK_START_STEPS: QuickStartStepId[] = [
  'upload-or-paste',
  'talk-it-through',
  'declare-target',
  'she-watches',
  'tailor-and-apply',
  'track-progress',
];

/** Where each step deep-links — App.tsx's own route table (SignIn/Goodbye
 *  live outside it; every value here is an EXISTING <Route path> already
 *  mounted, no new route added). */
export const QUICK_START_STEP_PATH: Record<QuickStartStepId, '/refine' | '/profiles' | '/jobs' | '/track'> = {
  'upload-or-paste': '/refine',
  'talk-it-through': '/refine',
  'declare-target': '/profiles',
  'she-watches': '/jobs',
  'tailor-and-apply': '/jobs',
  'track-progress': '/track',
};

export interface QuickStartStepCopy {
  id: QuickStartStepId;
  title: string;
  body: string;
}

/** Mira's own voice, en-US, zero jargon — the exact 6-step walkthrough from
 *  Jason's spec, warmed into first-person Mira copy rather than a dry
 *  numbered instruction list. */
export const QUICK_START_STEP_COPY: Record<QuickStartStepId, QuickStartStepCopy> = {
  'upload-or-paste': {
    id: 'upload-or-paste',
    title: 'Upload your resume or paste your profile',
    body: "Give me something real to start from — a resume file or your pasted profile text both work.",
  },
  'talk-it-through': {
    id: 'talk-it-through',
    title: 'Talk it through with me',
    body: 'Tell me the stories behind the bullet points, out loud or typed. That\'s how I find your real gold.',
  },
  'declare-target': {
    id: 'declare-target',
    title: "Tell me what you want to be found for",
    body: "Declare the roles you're after in Get Found, and I'll shape your profile around them.",
  },
  'she-watches': {
    id: 'she-watches',
    title: 'I watch the boards for you',
    body: "I keep checking your saved sources and bring back the roles worth your time, in Jobs.",
  },
  'tailor-and-apply': {
    id: 'tailor-and-apply',
    title: 'Tailor, then apply — always your call',
    body: "I'll draft a tailored version for any role you like. Nothing goes out until you send it.",
  },
  'track-progress': {
    id: 'track-progress',
    title: 'Track every application',
    body: "Every role you apply to lands in Track, so you always know where things stand.",
  },
};

/** Real, served state the caller already reads elsewhere (useHomeData et al)
 *  — this module never issues its own fetch. Null means genuinely unknown
 *  (read still in flight or failed), which this module treats identically to
 *  "not yet done" — never coerced to a fabricated zero OR a fabricated done. */
export interface QuickStartRealState {
  roleCount: number | null;
  targetRoleCount: number | null;
  applicationCount: number | null;
}

/**
 * Per-step done derivation — served-state in, boolean out, NEVER true without
 * a real positive count backing it. Steps 2 and 5 ("talk it through"/"tailor
 * and apply") have no independent server signal of their own (there is no
 * GET route that separately tracks "did the user have a voice session" or
 * "did the user tailor a CV") distinct from the signals steps 1/3/4/6 already
 * read — rather than fabricate a done state for them, they stay tied to the
 * SAME real signal as their sibling step in the same pairing, which is still
 * never an invented true: it is the honest read that "has a corpus" is the
 * only real signal available for the corpus-building pair, and "has tracked
 * jobs" is the only one for the discover/apply pair, both already true facts
 * about the user.
 */
export function isQuickStartStepDone(step: QuickStartStepId, state: QuickStartRealState): boolean {
  switch (step) {
    case 'upload-or-paste':
    case 'talk-it-through':
      // Either step's real signal is "does a corpus exist at all" —
      // GET /corpus/roles > 0. A null roleCount (unknown/failed read) is NOT done.
      return (state.roleCount ?? 0) > 0;
    case 'declare-target':
      // GET /presence/target-roles — the user has declared at least one
      // target role for Get Found.
      return (state.targetRoleCount ?? 0) > 0;
    case 'she-watches':
      // "She watches the boards" is a standing behavior, not a one-time
      // action with its own done flag — this module never invents a
      // completion state for an ongoing process. Ties to the same real
      // signal as 'declare-target' (declaring a target is what turns
      // watching on) so it never shows done before there is anything real
      // to watch FOR.
      return (state.targetRoleCount ?? 0) > 0;
    case 'tailor-and-apply':
    case 'track-progress':
      // GET /applications — at least one real tracked application.
      return (state.applicationCount ?? 0) > 0;
    default:
      return false;
  }
}

export interface QuickStartStepView extends QuickStartStepCopy {
  done: boolean;
  path: '/refine' | '/profiles' | '/jobs' | '/track';
}

/** The full, ordered step list with done-state resolved — the single function
 *  QuickStartGuide.tsx renders from, so the component never re-derives done
 *  logic inline (keeps the NO-INVENTION-tested surface in exactly one place). */
export function resolveQuickStartSteps(state: QuickStartRealState): QuickStartStepView[] {
  return QUICK_START_STEPS.map((id) => ({
    ...QUICK_START_STEP_COPY[id],
    done: isQuickStartStepDone(id, state),
    path: QUICK_START_STEP_PATH[id],
  }));
}

/**
 * Visibility decision (brief: "shown on Home for a signed-in user who hasn't
 * dismissed it"). Deliberately does NOT hide once every step reads done —
 * dismissal is the user's own explicit act (the X), never an automatic
 * disappearance the moment real progress happens to clear every box; a user
 * who imported a full corpus + declared targets + tracked jobs via another
 * path (e.g. a returning user restoring from export) still gets to see and
 * explicitly dismiss the guide once, not have it vanish out from under them.
 */
export function shouldShowQuickStart(dismissed: boolean): boolean {
  return !dismissed;
}

// =============================================================================
// localStorage persistence — mirrors onboardingStep.ts's readOnboardingProgress
// /writeOnboardingProgress exactly (the SAME hasWindow() guard, the same
// best-effort try/catch degrading to "never dismissed" rather than throwing
// in a locked-down/private-mode/non-browser context).
// =============================================================================

const DISMISSED_KEY = 'throughline:quickstart:dismissed:v1';

function hasWindow(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

/** True only on an explicit "raw === 'true'" match — any other stored value
 *  (corrupted, from a future format) reads as "never dismissed," the safe
 *  default that shows the guide rather than silently hiding it. */
export function readQuickStartDismissed(): boolean {
  if (!hasWindow()) return false;
  try {
    return window.localStorage.getItem(DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
}

export function writeQuickStartDismissed(dismissed: boolean): void {
  if (!hasWindow()) return;
  try {
    if (dismissed) {
      window.localStorage.setItem(DISMISSED_KEY, 'true');
    } else {
      // Nothing unrecoverable (the no-deletion spirit, applied to UI): the
      // "show it again" re-entry in Settings clears the flag entirely rather
      // than writing a tombstone value, so a fresh read behaves exactly like
      // a user who never dismissed it.
      window.localStorage.removeItem(DISMISSED_KEY);
    }
  } catch {
    // Best-effort only, same posture as onboardingStep.ts's writeOnboardingProgress.
  }
}
